import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

interface Row {
  token: string;
  email: string;
  workspace_id?: string | null;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_on: number;
  created_on: number;
}

export function AdminInvitations() {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => api.get<{ entries: Row[] }>('/api/invitations'),
  });
  const revoke = useMutation({
    mutationFn: (token: string) => api.delete(`/api/invitations/${encodeURIComponent(token)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });
  const resend = useMutation({
    mutationFn: (token: string) => api.post(`/api/invitations/${encodeURIComponent(token)}/resend`),
  });

  if (isPending) return <div className="card text-sm text-slate-500">Loading…</div>;
  const rows = data?.entries ?? [];

  return (
    <div className="card">
      <h3 className="text-base font-semibold">Invitations</h3>
      <table className="mt-3 min-w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2">Email</th>
            <th className="text-left py-2">Workspace</th>
            <th className="text-left py-2">Role</th>
            <th className="text-left py-2">Status</th>
            <th className="text-left py-2">Expires</th>
            <th className="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.token} className="border-b last:border-b-0">
              <td className="py-2">{r.email}</td>
              <td className="py-2 font-mono text-xs">{r.workspace_id ?? '—'}</td>
              <td className="py-2">{r.role}</td>
              <td className="py-2">{r.status}</td>
              <td className="py-2">{new Date(r.expires_on).toLocaleString()}</td>
              <td className="py-2 text-right">
                {r.status === 'pending' && (
                  <>
                    <button
                      className="btn-secondary text-xs mr-2"
                      onClick={() => resend.mutate(r.token)}
                      disabled={resend.isPending}
                    >
                      Resend
                    </button>
                    <button
                      className="btn-danger text-xs"
                      onClick={() => revoke.mutate(r.token)}
                      disabled={revoke.isPending}
                    >
                      Revoke
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-slate-400">
                No invitations.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
